import app from '../app';
import { gsap } from 'gsap';
import Flickity from 'flickity-bg-lazyload';
import throttle from 'lodash/throttle'
import { Video } from './videos'

export class AnimatedCursor {
  constructor() {
    this.cursorableElements = [];

    const originalCursor = document.getElementById('cursor');
    let cursor = originalCursor;

    if ( !cursor ) return;

    let cursorEventsAssigned = false;
    const $cursor = $(cursor);
    const trail = document.getElementById('cursor-trail');
    const $trail = $(trail);
    const defaultCursorSize = 4;
    const defaultTrailSize = 50;
    let cursorSize = defaultCursorSize;
    let trailSizeX = defaultTrailSize;
    let trailSizeY = trailSizeX;
    const x = app.windowWidth / 2 + 'px';
    const y = app.windowHeight / 2 + 'px';
    let transitionInProgress = false;
    let pauseTrail = false;
    let preventBulge = false;
    const defaultTrailDuration = 0.3;
    const mouse = {
      x: 0,
      y: 0
    };
    const cursorReelTimers = [];

    const setTransitionInProgress = () => {
      transitionInProgress = true;
      app.$html.addClass('transition-in-progress');
    }

    const setTransitionFinished = () => {
      transitionInProgress = false;
      app.$html.removeClass('transition-in-progress');
    }

    const emptyCursor = () => {
      $trail.empty()
      app.$body.removeClass('cursor-handoff-active');
      trailSizeX = defaultTrailSize;
      trailSizeY = trailSizeX;
    }

    const resetCursor = (duration) => {
      const transitionWasInProgress = transitionInProgress;

      transitionInProgress = false;

      emptyCursor();

      if ( !duration ) duration = 0;

      if ( transitionWasInProgress ) {
        gsap.killTweensOf(trail);
        gsap.to(trail, {
          duration: 0,
          left: mouse.x,
          top: mouse.y,
          width: 0,
          height: 0,
          borderRadius: '50%'
        });
      }

      gsap.killTweensOf(trail);
      gsap.to(trail, {
        duration: duration,
        width: trailSizeX,
        height: trailSizeY,
        left: mouse.x - (trailSizeX / 2),
        top: mouse.y - (trailSizeY / 2),
      });

      $trail.removeClass('cursor-trail--image cursor-trail--video cursor-trail--blend-mode-normal has-cursor--external-link');
      $cursor.removeClass('cursor--external-link');
    }

    let hasDoneInitialMove = false;

    const moveCursor = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;

      if ( !hasDoneInitialMove ) {
        cursor.style.opacity = 1;
        trail.style.opacity = 1;
      }

      hasDoneInitialMove = true;

      if ( transitionInProgress ) return;

      gsap.to(cursor, {
        duration: 0,
        left: mouse.x - (cursorSize / 2),
        top: mouse.y - (cursorSize / 2),
        ease: 'elastic(0.8, 0.75)'
      });

      if ( !pauseTrail ) {
        gsap.to(trail, {
          duration: 1,
          left: mouse.x - (trailSizeX / 2),
          top: mouse.y - (trailSizeY / 2),
          ease: 'elastic(0.8, 0.75)'
        });
      }

      if ( e.target.tagName == 'IFRAME' ) {
        document.body.classList.add('cursor-is-hidden');
      } else {
        document.body.classList.remove('cursor-is-hidden');
      }
    }

    const bulgeCursor = () => {
      if ( preventBulge ) return;

      gsap.killTweensOf(trail);
      gsap.to(trail, {
        duration: 0.15,
        width: (trailSizeX / 0.7),
        height: (trailSizeY / 0.7),
        left: mouse.x - ((trailSizeX / 0.7) / 2),
        top: mouse.y - ((trailSizeY / 0.7) / 2),
        onComplete: () => {
          gsap.to(trail, {
            duration: 0.15,
            width: trailSizeX,
            height: trailSizeY,
            left: mouse.x - (trailSizeX / 2),
            top: mouse.y - (trailSizeY / 2)
          });
        }
      });
    }

    const resizeCursor = (options = {}, callback) => {
      const width = options.width
      const height = options.height === undefined ? options.width : options.height
      const duration = options.duration === undefined ? 0.3 : options.duration
      const ease = options.ease
      const borderRadius = options.borderRadius

      trailSizeX = width
      trailSizeY = height

      gsap.killTweensOf(trail);
      gsap.to(trail, {
        duration: duration,
        ease: ease,
        width: width,
        height: height,
        left: mouse.x - (trailSizeX / 2),
        top: mouse.y - (trailSizeY / 2),
        onComplete: () => {
          if (typeof callback === 'function') {
            callback()
          }
        }
      });
    }

    // When pausing Turbo, you can run into buggy behavior if a user pressed the back button before
    // the async animateOut function completes, which results in the newly navigated-to page (via the back button)
    // getting rendered with the async page's `event.detail.resume()` Turbo body. In this checkForHistory function,
    // we try to fix that edge case.
    let historyHasChangedWhileAnimating = false;
    const checkForHistoryChangeWhilePausingTurboRender = (e) => {
      historyHasChangedWhileAnimating = true;
    }

    const linkHandoffClick = (e) => {
      e.preventDefault();

      // TODO: there's an edge case bug when you click twice quickly on a link, it messes up the transition.

      const cursorable = e.target.closest('[data-cursorable]');
      const handoffPath = cursorable.dataset.handoffPath;

      setTransitionInProgress();

      const animateOut = async () => {
        return new Promise((resolve) => {
          gsap.killTweensOf(trail);
          gsap.to(trail, {
            duration: 0.6,
            width: app.windowWidth * 2,
            height: app.windowHeight * 2,
            top: (app.windowHeight * 2) / -4,
            left: (app.windowWidth * 2) / -4,
            // borderRadius: 0,
            onComplete: () => {
              resolve();
            }
          });
        });
      }

      removeCursorableEventListeners();

      // First clear the turbo, otherwise it looks like the video jumps when turbo shows the cached preview
      Turbo.clearCache();
      Turbo.visit(handoffPath);

      const animateOutPromise = animateOut();

      // https://turbo.hotwired.dev/handbook/drive#pausing-rendering
      document.addEventListener('turbo:before-render', async (e) => {
        e.preventDefault();

        window.addEventListener('popstate', checkForHistoryChangeWhilePausingTurboRender, { once: true });

        await animateOutPromise;

        // In order to prevent videos from looking like they restart when you land on the project#show page,
        // we pull the project header out of the cursor and append it into the project#show page.
        const newProjectHeader = e.detail.newBody.querySelector('.project__header');
        const $oldProjectHeader = $(trail.querySelector('.project__header'));
        newProjectHeader.remove();

        e.detail.newBody.classList.add('has-performed-page-animation');

        if ( historyHasChangedWhileAnimating ) {
          location.reload();
        } else {
          e.detail.resume();

          document.addEventListener('turbo:load', (e) => {
            $oldProjectHeader.appendTo('#project-show__header-wrapper');
            resetCursor(defaultTrailDuration);

            $('.scroll-reveal').each((index, el) => {
              const $el = $(el);
              const offset = $el.offset().top;

              if ( offset <= app.windowHeight + app.scrollTop ) {
                $el.removeClass('scroll-reveal');
              }
            });
          }, { once: true });
          historyHasChangedWhileAnimating = false;
        }

        window.removeEventListener('popstate', checkForHistoryChangeWhilePausingTurboRender, { once: true });
      }, { once: true })
    }

    const cursorHandoff = (element) => {
      const handoff = element.querySelector('[data-cursorable-handoff-element]');
      const cursorable = element.closest('[data-cursorable]');

      if ( !handoff ) return;

      $trail.html(handoff.innerHTML);

      app.$body.addClass('cursor-handoff-active');

      $(cursorable).one('click', linkHandoffClick);
    }

    const disableCursorHandoff = (element) => {
      const cursorable = element.closest('[data-cursorable]');
      resetCursor(defaultTrailDuration);
      $(cursorable).off('click', linkHandoffClick);
    }

    const cursorHighlight = (element) => {
      pauseTrail = true;

      const cursorable = element.closest('[data-cursorable]');
      const bb = cursorable.getBoundingClientRect();

      gsap.killTweensOf(trail);
      gsap.to(trail, {
        duration: 0.3,
        width: bb.width,
        height: bb.height,
        left: bb.left,
        top: bb.top,
        borderRadius: 0
      });
    }

    const cursorImage = (element) => {
      const url = element.getAttribute('data-cursorable-media-url');
      const $media = $('<img src="' + url + '" class="cursor-trail__image">');
      const mediaRatio = parseFloat(element.getAttribute('data-media-ratio'));
      const cursorableSize = element.getAttribute('data-cursorable-size');

      if ( cursorableSize == 'small' ) {
        trailSizeX = Math.min((app.windowWidth / 4), 300);
      } else {
        trailSizeX = Math.max((app.windowWidth / 3), 300);

        // Vertical images
        if ( mediaRatio > 1 ) {
          trailSizeX = Math.min((app.windowWidth / 4), 300);
        }
      }

      trailSizeY = trailSizeX * mediaRatio;
      preventBulge = true;

      $trail.addClass('cursor-trail--image').html($media);
    }

    const cursorVideo = (element) => {
      const $element = $(element);
      const url = element.getAttribute('data-cursorable-media-url');
      const posterImageUrl = element.getAttribute('data-cursorable-poster-image-url');
      const posterImageAttr = posterImageUrl ? 'poster="' + posterImageUrl + '"' : '';
      const $media = $('<video src="' + url + '" ' + posterImageAttr + ' class="cursor-trail__video" muted playsinline autoplay loop>');
      const mediaRatio = parseFloat(element.getAttribute('data-media-ratio'));
      const currentTime = parseFloat($element.data('current-video-time') || 0);

      const cursorableSize = element.getAttribute('data-cursorable-size');

      if ( cursorableSize == 'small' ) {
        trailSizeX = Math.min((app.windowWidth / 4), 300);
      } else {
        trailSizeX = Math.max((app.windowWidth / 2.5), 300);

        // Vertical images
        if ( mediaRatio > 1 ) {
          trailSizeX = Math.max((app.windowWidth / 3.5), 300);
        }
      }

      trailSizeY = trailSizeX * mediaRatio;
      preventBulge = true;

      $trail.addClass('cursor-trail--video').html($media);

      const video = $media[0];

      video.addEventListener('loadedmetadata', () => {
        video.currentTime = currentTime;
      }, { once: true });

      let playPromise = $media[0].play();

      if ( playPromise !== undefined ) {
        playPromise.then(function() {
          // Automatic playback started
        }).catch(function(error) {
          // Auto-play was prevented
          console.warn('Error playing video', error);
        });
      }
    }

    const cursorExternalLink = (element) => {
      $cursor.addClass('cursor--external-link');
      $trail.addClass('has-cursor--external-link');
    }

    const cursorClass = (element) => {
      const cssClass = $(element).closest('[data-cursorable="css-class"]').attr('data-cursorable-css-class');

      $cursor.addClass(`cursor--css-class cursor--css-class-${cssClass}`);
      $trail.addClass(`cursor-trail--css-class cursor-trail--css-class-${cssClass}`);
    }

    const cursorSlideshow = (element) => {
      const $carousel = $(element);
      const $mm = $carousel.closest('.magic-module');
      const moduleId = $mm.attr('id');
      const flkty = Flickity.data($carousel[0]);
      const totalSlides = flkty.slides.length;

      if ( totalSlides < 2 ) return;

      const currentIndex = flkty.selectedIndex + 1;
      const labelDiv = $('<div class="cursor__slideshow-label">' + currentIndex + ' of ' + totalSlides + '</div>');
      $trail.addClass('cursor-trail--slideshow').data('moduleId', moduleId).html(labelDiv);
    }

    const resetToStart = (videoEl) => {
      if (!videoEl) return;
      const setZero = () => {
        try { videoEl.currentTime = 0; } catch (_) {}
      };
      if (videoEl.readyState >= 1) { // HAVE_METADATA
        setZero();
      } else {
        videoEl.addEventListener('loadedmetadata', setZero, { once: true });
      }
    };

    const cursorReel = (element, options = {}) => {
      const $reel = $(element).closest('[data-cursorable="reel"]');
      const $heroReelOuterWrapper = $reel.closest('.hero-reel-module__reel-outer-wrapper');
      const $mobilePlayPause = $('#hero-reel-module__mobile-play-pause');
      const $slides = $reel.find('.hero-reel-module__slide');
      const $current = $slides.filter('.active').first();
      const $nextSlide = $current.nextWrap();
      const nextDuration = parseInt($nextSlide.attr('data-duration')) || 250;
      const irisSwipeDuration = 1; // seconds (this is the duration for the fancy iris transition in GSAP)
      const $debugDuration = $current.find('.hero-reel-module__slide__duration');
      const $debugTotal = $current.find('.hero-reel-module__slide__total');

      // console.warn('cursorReel')

      // Initialize lazy videos for current and next slides (if placeholders exist)
      const onLoaded = (videoEl, $video, placeholderEl) => {
        const duration = Math.round(videoEl.duration * 1000);
        // console.log('set duration', duration);
        $(videoEl).closest('.hero-reel-module__slide').attr('data-duration', duration);
      };

      $current.add($nextSlide)
        .find('.lazy-video-placeholder')
        .removeClass('lazy-video-delay-initialization')
        .each(function () {
          new Video(this, onLoaded);
        });

      // Now (re)select videos after potential lazy init above
      const $currentVideo = $current.find('video');
      // console.log('$currentVideo', $currentVideo)
      const $nextVideo = $nextSlide.find('video');

      // helper: only play once metadata is available (for current slide)
      const playWhenMetadataReady = (videoEl) => {
        if (!videoEl) return;
        const tryPlay = () => {
          // guard: only play if this slide is still current and reel isn't paused
          if (!$current.hasClass('active')) return;
          if ($reel.data('paused')) return;
          videoEl.play?.().catch(() => {});
        };
        if (videoEl.readyState >= 1) { // HAVE_METADATA
          tryPlay();
        } else {
          videoEl.addEventListener('loadedmetadata', tryPlay, { once: true });
        }
      };

      const nextIsImage = $nextSlide.attr('data-item-type') == 'image';

      // Fancy swipe if exactly one of current/next has a video
      let shouldDoFancySwipe = ($currentVideo.length && !$nextVideo.length) || (!$currentVideo.length && $nextVideo.length);
      const minDurationForFancySwipe = 1000;

      if ( nextIsImage && nextDuration < minDurationForFancySwipe ) {
          shouldDoFancySwipe = false;
      }

      // Base duration from attribute (full video length) or 4000ms fallback
      const baseDurationMs = parseInt($current.attr('data-duration'), 10) || 4000;
      // Animations needs at least 250ms to work properly
      // Videos should be closer to 4 seconds minimum, but this needs to be set on the video file itself.
      const minimumDuration = 250;

      // If this slide has a video, use remaining time; otherwise use base duration
      let duration = baseDurationMs;
      if ($currentVideo.length) {
        const v = $currentVideo[0];
        const currentTimeMs = (v?.currentTime || 0) * 1000;
        // console.log('currentTimeMs', currentTimeMs)
        duration = Math.max(0, Math.round(baseDurationMs - currentTimeMs));
        // if (shouldDoFancySwipe) duration -= 500; // kept for non-video timing; video uses 'ended' below
      }

      duration = Math.max(minimumDuration, duration);

      if ($debugDuration.length) {
        $debugDuration.html(` ${(duration / 1000).toFixed(2)}s remaining`);
        $debugTotal.html(` ${(baseDurationMs / 1000).toFixed(2)}s total`)
      }

      const $mm = $reel.closest('.magic-module');
      const moduleId = $mm.attr('id');
      const clickEvent = options.clickEvent || false;
      let isPlaying = !$reel.data('paused');
      const $videos = $reel.find('video');
      const siteContainerOffset = $('.site-container').first().offset().left;
      const wrapperOffsetTop = $heroReelOuterWrapper.offset().top;

      // console.log('duration (ms, remaining if video):', duration);

      cursorReelTimers.forEach(function(timer) {
        if (timer) window.clearTimeout(timer);
      });

      if (clickEvent) {
        if (isPlaying) {
          $reel.data('paused', true);
          isPlaying = false;
          $videos.each(function() { this.pause?.(); });
        } else {
          $reel.data('paused', false);
          isPlaying = true;
          // play all, but defer current slide until metadata
          $videos.each(function() {
            if ($currentVideo.length && this === $currentVideo[0]) {
              playWhenMetadataReady(this);
            } else {
              this.play?.();
            }
          });
        }
      }

      const status = isPlaying ? 'Pause' : 'Play';
      const labelDiv = $('<div class="cursor__slideshow-label">' + status + '</div>');
      const cursorIsMouseEntered = $reel.data('reel-cursor-is-active');

      if (cursorIsMouseEntered) {
        $trail.addClass('cursor-trail--reel').data('moduleId', moduleId).html(labelDiv);
      }

      $mobilePlayPause.html(status);

      if (!isPlaying) return;

      const $inactiveVideos = $slides.not($current).not($nextSlide).find('video');
      $inactiveVideos.each(function() { this.pause?.(); });

      const $activeVideos = $current.find('video');
      $activeVideos.each(function() {
        if ($currentVideo.length && this === $currentVideo[0]) {
          playWhenMetadataReady(this);
        } else {
          this.play?.();
        }
      });

      // --- ADVANCE LOGIC: wait for video 'ended' or fallback timer ---
      const advanceNow = () => {
        $current.removeClass('active').css({ zIndex: 2 });
        $slides.not($current).css({ zIndex: 0 });
        $nextSlide.css({ zIndex: 3 });

        if (shouldDoFancySwipe) {
          $current.css({ zIndex: 1 });

          const getXPos = () => {
            let pos = mouse.x - cursorSize - wrapperOffsetTop + app.scrollTop;
            if (pos < 0) pos = 0;
            if (pos > $heroReelOuterWrapper.width()) pos = $heroReelOuterWrapper.width();
            if (app.breakpointIsMobile()) pos = $heroReelOuterWrapper.width() / 2;
            return pos;
          };

          const getYPos = () => {
            let pos = mouse.y - cursorSize - wrapperOffsetTop + app.scrollTop;
            if (pos < 0) pos = 0;
            if (pos > $heroReelOuterWrapper.height()) pos = $heroReelOuterWrapper.height();
            if (app.breakpointIsMobile()) pos = $heroReelOuterWrapper.height() / 2;
            return pos;
          };

          $nextSlide.css({ clipPath: `circle(0% at ${getXPos()}px ${getYPos()}px)` });

          gsap.to($nextSlide, {
            duration: irisSwipeDuration,
            ease: 'linear',
            clipPath: `circle(150% at ${getXPos()}px ${getYPos()}px)`,
            onStart: () => {
              $nextVideo.each(function () {
                resetToStart(this);
              });

              window.setTimeout(function() {
                $nextSlide.addClass('active');
                cursorReel(element);
              }, 100);
            }
          });
        } else {
          $nextVideo.each(function () {
            resetToStart(this);
          });
          $nextSlide.addClass('active');
          cursorReel(element);
        }
      };

      if ($nextVideo.length) $nextVideo[0].currentTime = 0;

      if ($currentVideo.length) {
        // Wait for full playback
        const v = $currentVideo[0];

        // If paused midway, we'll still advance only when it ends after resume.
        // Safety fallback to avoid a hang if 'ended' never fires (e.g., network/codec issues).
        const safetyMs = Math.max(20000, baseDurationMs + 5000); // ≥20s or base+5s
        const safetyTimer = window.setTimeout(() => {
          advanceNow();
        }, safetyMs);
        cursorReelTimers.push(safetyTimer);

        v.addEventListener('ended', () => {
          window.clearTimeout(safetyTimer);
          advanceNow();
        }, { once: true });

        // Ensure playback starts once metadata is available
        playWhenMetadataReady(v);
      } else {
        // No video: keep existing timer behavior
        cursorReelTimers.push(window.setTimeout(advanceNow, duration));
      }

      app.teardown.push(() => {
        cursorReelTimers.forEach(function(timer) {
          if (timer) window.clearTimeout(timer);
        });
      });
    };

    const onCursorableHover = (e) => {
      const element = e.target;
      const cursorable = element.closest('[data-cursorable="handoff"]') || element.closest('[data-cursorable="reel"]') || element;
      const cursorableType = cursorable.dataset.cursorable;
      const label = cursorable.dataset.cursorLabel;
      const cursorableBlendMode = element.getAttribute('data-cursorable-blend-mode');

      if ( cursorableBlendMode == 'normal' ) {
        $trail.addClass('cursor-trail--blend-mode-normal');
      }

      cursorSize = 0;
      trailSizeX = 100;
      trailSizeY = trailSizeX;

      if ( cursorableType == 'handoff' ) {
        trailSizeX = 300;
        trailSizeY = trailSizeX;
        cursorHandoff(element);
      } else if ( cursorableType == 'highlight' ) {
        cursorHighlight(element);
        return;
      } else if ( cursorableType == 'image' ) {
        cursorImage(element);
        if ( element.dataset.handoffPath ) {
          $(element).one('click', function() {
            resizeCursor({ width: 0, ease: 'power3.out' }, () => {
              cursorHandoff(element);
              resizeCursor({ width: 300, ease: 'power3.in', borderRadius: '50%' }, () => {
                $(element).trigger('click');
              });
            });
            removeCursorableEventListeners();
          });
        }
      } else if ( cursorableType == 'video' ) {
        cursorVideo(element);
      } else if ( cursorableType == 'external-link' ) {
        cursorExternalLink(element);
      } else if ( cursorableType == 'css-class' ) {
        cursorClass(element);
      } else if ( cursorableType == 'reel' ) {
        $(cursorable).data('reel-cursor-is-active', true);
        cursorReel(element);
      } else if ( cursorableType == 'slideshow' ) {
        cursorSlideshow(element);
      } else {
        if ( element.closest('[data-cursorable="handoff"]') ) return;

        if ( cursorableType == 'enlarge' ) {
          trailSizeX = 300;
          trailSizeY = trailSizeX;
        }
      }

      if ( label ) {
        const labelDiv = document.createElement('div');
        const labelText = document.createTextNode(label);
        labelDiv.appendChild(labelText);
        labelDiv.classList.add('cursor__label');
        trail.append(labelDiv);
      }

      gsap.killTweensOf(trail);
      gsap.to(trail, {
        duration: 0.3,
        width: trailSizeX,
        height: trailSizeY,
        left: mouse.x - (trailSizeX / 2),
        top: mouse.y - (trailSizeY / 2)
      });
    }

    const offCursorableHover = (e) => {
      // return;

      const element = e.target;
      const $element = $(element);
      const cursorableType = element.dataset.cursorable;

      preventBulge = false;

      $trail.removeClass('cursor-trail--blend-mode-normal');

      if ( cursorableType == 'handoff' ) {
        disableCursorHandoff(element);
        return;
      } else if ( cursorableType == 'image' ) {
        $trail.removeClass('cursor-trail--image').empty();
      } else if ( cursorableType == 'video' ) {
        let video = $trail.find('video')[0];

        if ( video ) {
          $element.data('current-video-time', video.currentTime);
          video.pause();
          video.src = '';
          video.remove();
          video = null;
        }

        $trail.removeClass('cursor-trail--video').empty();
      } else if ( cursorableType == 'external-link' ) {
        $cursor.removeClass('cursor--external-link').empty();
        $trail.removeClass('has-cursor--external-link');
      } else if ( cursorableType == 'css-class' ) {
        $cursor
          .removeClass('cursor--external-link cursor--css-class')
          .removeClass(function (index, className) {
            return (className.match(/(^|\s)cursor--css-class-\S+/g) || []).join(' ');
          })
          .empty();
        $trail
          .removeClass('cursor-trail--css-class')
          .removeClass(function (index, className) {
          return (className.match(/(^|\s)cursor-trail--css-class-\S+/g) || []).join(' ');
        });
      } else if ( cursorableType == 'reel' ) {
        $element.data('reel-cursor-is-active', false);
        $cursor.empty();
        emptyCursor();
        $trail.removeClass('cursor-trail--reel');
      } else if ( cursorableType == 'slideshow' ) {
        $trail.removeClass('cursor-trail--slideshow').data('moduleId', undefined).empty();
      } else if ( element.closest('[data-cursorable="handoff"]') ) {
        return;
      }

      trailSizeX = defaultTrailSize;
      trailSizeY = trailSizeX;

      gsap.killTweensOf(trail);
      gsap.to(trail, {
        duration: 0.3,
        width: trailSizeX,
        height: trailSizeY,
        left: mouse.x - (trailSizeX / 2),
        top: mouse.y - (trailSizeY / 2)
      });
    }

    const skipHeroReelAnimation = () => {
      if ( app.isHomePage() && !app.$body.hasClass('home-page-intro-animation') ) {
        app.$body.addClass('show-home-logo-reveal home-logo-reveal--no-transition');
        const $heroReelModule = $('#application-wrapper > .hero-reel-module').first();
        if ( $heroReelModule.length ) {
          homePageHeroReelModuleAnimation($heroReelModule, { skipAnimation: true });
        }
      }
    }

    const initialize = () => {
      if ( app.breakpointIsMobile() ) {
        resetCursor();
        skipHeroReelAnimation();
        return;
      }

      this.setCursorableElements();

      for (var i = this.cursorableElements.length - 1; i >= 0; i--) {
        this.cursorableElements[i].addEventListener('mouseenter', onCursorableHover);
        this.cursorableElements[i].addEventListener('mouseleave', offCursorableHover);
      }

      app.$html.removeClass('transition-in-progress');

      resetCursor();
      skipHeroReelAnimation();
    }

    const removeCursorableEventListeners = () => {
      for (var i = this.cursorableElements.length - 1; i >= 0; i--) {
        this.cursorableElements[i].removeEventListener('mouseenter', onCursorableHover);
        this.cursorableElements[i].removeEventListener('mouseleave', offCursorableHover);
      }

      this.cursorableElements = [];
    }

    const setMouseMoveListeners = () => {
      if ( app.breakpointIsMobile() ) return;

      window.addEventListener('mousemove', moveCursor, { passive: true });
      window.addEventListener('mousedown', bulgeCursor);
      cursorEventsAssigned = true;
    }

    const cursorShrinkAnimation = () => {
      gsap.to(trail, {
        duration: 0.5,
        delay: 0.4,
        width: defaultTrailSize,
        height: defaultTrailSize,
        left: mouse.x - (defaultTrailSize / 2),
        top: mouse.y - (defaultTrailSize / 2),
        ease: 'expo.out',
        onStart: () => {
          trailSizeX = defaultTrailSize;
          trailSizeY = trailSizeX;
          app.$body.removeClass('home-page-intro-animation--begin').addClass('show-home-logo-reveal');
        },
        onComplete: () => {
          app.$body.addClass('home-page-intro-animation--complete');
          setMouseMoveListeners();
          initialize();
          app.reflow();
        }
      });

      window.removeEventListener('mousemove', cursorShrinkAnimation, { once: true });
      window.removeEventListener('keyup', cursorShrinkAnimation, { once: true });
      window.removeEventListener('scroll', cursorShrinkAnimation);
    }

    const setReelDimensions = ($heroReelOuterWrapper) => {
      $heroReelOuterWrapper[0].style.setProperty('--reel-width', $heroReelOuterWrapper.width() + 'px');
      $heroReelOuterWrapper[0].style.setProperty('--reel-height', $heroReelOuterWrapper.height() + 'px');
    }

    const homePageHeroReelModuleAnimation = ($heroReelModule, options = {}) => {
      const $heroReelOuterWrapper = $heroReelModule.find('.hero-reel-module__reel-outer-wrapper');
      const $heroReelWrapper = $heroReelModule.find('.hero-reel-module__reel-wrapper');
      const $heroReel = $heroReelModule.find('.hero-reel-module__reel');
      const heroReelWidth = $heroReelOuterWrapper.width();
      const heroReelHeight = $heroReelOuterWrapper.height();
      const $headerLogo = $('#header__desktop-logo');
      const $headerAltLogo = $('#header__desktop-logo--alt');
      const $headerNav = $('#header__nav');
      const skipAnimation = options.skipAnimation;

      trailSizeX = 140; // Match size to _hero_reel_module.scss width
      trailSizeY = 140;

      const $videos = $heroReel.find('video');
      if ( $videos.length ) {
        $videos.each(function() {
          const $video = $(this);
          $video[0].addEventListener('loadedmetadata', () => {
            const duration = Math.round($video[0].duration * 1000);
            $video.closest('.hero-reel-module__slide').attr('data-duration', duration);
          });
        }, { once: true });
      }

      if ( !skipAnimation ) {
        mouse.x = app.windowWidth / 2;
        mouse.y = app.windowHeight / 2;
      }

      if ( !skipAnimation ) {
        gsap.killTweensOf(trail);
        gsap.to(trail, {
          duration: 0,
          opacity: 1,
          width: app.windowWidth - 30,
          height: app.windowWidth - 30,
          left: 15,
          top: app.windowHeight / 4,
        });
      }

      if ( !skipAnimation ) {
        $heroReelOuterWrapper.css({
          height: heroReelHeight
        })

        $heroReelWrapper.css({
          width: trailSizeX,
          height: trailSizeY,
          left: (heroReelWidth / 2) - (trailSizeX / 2),
          top: (heroReelHeight / 2) - (trailSizeY / 2),
        })
      } else {
        // Skipping the animation
        $headerLogo.add($headerNav).css({ opacity: 1 });
        $headerAltLogo.remove();
        $heroReelWrapper.add($headerLogo).add($headerNav).addClass('no-transition');
        $heroReelWrapper.css({
          width: '100%',
          height: '100%',
          left: 0,
          top: 0,
          borderRadius: 0,
        });
        window.setTimeout(() => {
          $heroReelWrapper.add($headerLogo).add($headerNav).removeClass('no-transition');
        }, 1);
      }

      $heroReel.css({
        width: heroReelWidth,
        height: heroReelHeight,
        position: 'absolute',
      })

      setReelDimensions($heroReelOuterWrapper);

      app.$body.addClass('home-page-intro-animation home-page-intro-animation--begin');

      window.setTimeout(() => {
        gsap.to(trail, {
          duration: skipAnimation ? 0 : 0.5,
          delay: skipAnimation ? 0 : 0.4,
          width: trailSizeX,
          height: trailSizeY,
          left: $heroReelWrapper[0].getBoundingClientRect().left,
          top: $heroReelWrapper[0].getBoundingClientRect().top,
          ease: 'expo.out',
          onStart: () => {
            window.setTimeout(() => {
              trail.classList.add('no-transition');
              trail.style.opacity = 0;
              trailSizeX = defaultTrailSize;
              trailSizeY = trailSizeX;
              app.$body.removeClass('home-page-intro-animation--begin').addClass('show-home-logo-reveal home-page-intro-animation--reveal-started');
            }, skipAnimation ? 0 : 500);
          },
          onComplete: () => {
            trail.classList.remove('no-transition');

            window.setTimeout(() => {
              const reelElement = $('[data-cursorable="reel"]').get(0);

              setMouseMoveListeners();
              initialize();
              app.reflow();
              app.$body.addClass('home-page-intro-animation--reveal-completed');

              if ( skipAnimation ) {
                app.$body.removeClass('home-page-intro-animation--begin home-page-intro-animation--reveal-started').addClass('show-home-logo-reveal');
              }

              if ( reelElement ) {
                reelElement.addEventListener('mousemove', onCursorableHover, { once: true });
                cursorReel(reelElement);
              }
            }, skipAnimation ? 0 : 750);
          }
        });
      }, skipAnimation ? 0 : 500);

      app.$window.on(
        'resize.homePageHeroReelModuleAnimation',
        throttle(() => setReelDimensions($heroReelOuterWrapper), 250)
      );

      app.teardown.push(() => {
        app.$body.removeClass('home-page-intro-animation home-page-intro-animation--begin');
        app.$window.off('scroll.homePageHeroReelModuleAnimation');
      });
    };

    const homePageLegacyAnimation = () => {
      app.$body.addClass('home-page-intro-animation home-page-intro-animation--begin');

      mouse.x = app.windowWidth / 2;
      mouse.y = app.windowHeight / 2;
      trailSizeX = app.windowWidth - 30;
      trailSizeY = trailSizeX;

      gsap.killTweensOf(trail);
      gsap.to(trail, {
        duration: 0,
        opacity: 1,
        width: trailSizeX,
        height: trailSizeY,
        left: 15,
        top: app.windowHeight / 4
      });

      window.addEventListener('mousemove', cursorShrinkAnimation, { once: true });
      window.addEventListener('keyup', cursorShrinkAnimation, { once: true });

      // Use a setTimeout to assign the scroll listener so the initial call to scroll top doesn't trigger it
      window.setTimeout(() => {
        window.addEventListener('scroll', cursorShrinkAnimation);
      }, 10);

      app.teardown.push(() => {
        app.$body.removeClass('home-page-intro-animation home-page-intro-animation--begin');
      });
    };

    const homePageAnimation = () => {
      const $heroReelModule = $('#application-wrapper > .hero-reel-module').first();

      window.scrollTo(0, 0);

      if ( $heroReelModule.length ) {
        homePageHeroReelModuleAnimation($heroReelModule);
      } else {
        homePageLegacyAnimation();
      }
    }

    app.pageLoad.push(() => {
      initialize();
      if ( !cursorEventsAssigned ) setMouseMoveListeners();
    });

    if ( app.isHomePage() && window.location.hash != '#content' ) {
      homePageAnimation();
    } else {
      setMouseMoveListeners();
      initialize();
    }

    app.teardown.push(() => {
      if ( !transitionInProgress ) {
        emptyCursor();
        resetCursor(defaultTrailDuration);
      }
      removeCursorableEventListeners();
    });

    app.$document.on('click.cursorReel', '[data-cursorable="reel"]', (e) => {
      cursorReel(e.target, { clickEvent: true });
    });
  }

  setCursorableElements() {
    this.cursorableElements = [
      ...document.links,
      ...document.querySelectorAll('[data-cursorable]')
    ];
  }
}

// Export an init function that looks for and instantiates the module on pageload
export default (() => {
  window.addEventListener('DOMContentLoaded', () => {
    if ( app.breakpointIsMobile() ) {
      app.$document.on('click', '[data-handoff-path]', (e) => {
        const $target = $(e.currentTarget);
        const handoffPath = $target.attr('data-handoff-path');

        Turbo.visit(handoffPath);
      });
    } else {
      app.$document.on('mouseenter', 'iframe', () => {
        app.$body.addClass('window-not-focused');
      });

      app.$document.on('mouseleave', 'iframe', () => {
        app.$body.removeClass('window-not-focused');
      });

      app.$document.on('mouseleave', () => {
        app.$body.addClass('window-not-focused');
      });

      app.$document.on('mouseenter', () => {
        app.$body.removeClass('window-not-focused');
      });
    }

    new AnimatedCursor();
  });
})();
